// src/components/Form/LeaveAllocation.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowLeft, FaEdit, FaTrash, FaClipboardList, FaCheckCircle,
  FaExclamationTriangle, FaCog, FaBuilding, FaMapMarkerAlt, FaTimes
} from 'react-icons/fa';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { checkIsAdmin, checkIsGlobalAdmin } from "../../utils/authUtils";
import useCustomization from '../../hooks/useCustomization';
import { UserContext } from '../../Context/UserContext';
import './CreateCustomerPage.css';

const LeaveAllocation = () => {
  const navigate = useNavigate();
  const { user, getHeaders } = useContext(UserContext);
  const { visibleFields, isFieldVisible, handleCustomizationRefresh } = useCustomization('Leave Allocation');
  const containerRef = useRef(null);
  const [allocations, setAllocations] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [form, setForm] = useState({
    leave_type_id: '',
    monthly_credit: 0,
    yearly_credit: 0,
    credit_unit: 'DAYS',
    is_active: true
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedAddBranches, setSelectedAddBranches] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);

  const activeBranchLocalUI = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActiveUI = activeBranchLocalUI.toLowerCase() !== 'all branches' && activeBranchLocalUI.toLowerCase() !== 'all' && activeBranchLocalUI !== '';
  const showCompanyAssign = !isSpecificBranchActiveUI && (isGroupAdmin || isCompanyAdmin);
  const showBranchAssign = !isSpecificBranchActiveUI && (isGroupAdmin || isCompanyAdmin);

  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          const role = userObj.role || userObj.UserType || "";
          const roleNorm = role.toLowerCase().replace(/[\s_]/g, '');
          const isGA = checkIsGlobalAdmin(userObj);
          const isCA = roleNorm === 'companyadmin' || roleNorm === 'admin' || roleNorm === 'manager';
          const isAdmin = isGA || isCA;
          setIsCompanyAdmin(isAdmin);
          setIsGroupAdmin(isGA);
          const userLocal = userObj;
          const activeContext = localStorage.getItem('active_company');
          let comps = [];
          if (userLocal.companies && userLocal.companies.length > 0) {
            comps = userLocal.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
          } else if (userLocal.company_name || userLocal.company) {
            comps = [userLocal.company_name || userLocal.company];
          } else {
            const currentBaseUrl = baseUrl || '';
            const res = await axios.get(`${currentBaseUrl}/api/company-details`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            const details = res.data.companyDetails || [];
            comps = details.map(d => d.restaurantName || d.company_name).filter(n => n);
          }
          const finalComps = comps.length > 0 ? [...new Set(comps)] : [];
          setAllCompanies(finalComps);
          if (activeContext && activeContext !== 'All') {
            setSelectedCompanies([activeContext || userLocal.company_name || ""]);
          } else if (activeContext === 'All' || !activeContext) {
            setSelectedCompanies([]);
          }
          if (role) {
            const headers = {};
            if (userLocal.company_name) headers['X-Company-Name'] = userLocal.company_name;
            if (userLocal.branch_name) headers['X-Branch-Name'] = userLocal.branch_name;
            const url = baseUrl ? `${baseUrl}/api/role-permissions?role=${role}` : `/api/role-permissions?role=${role}`;
            const response = await axios.get(url, { headers });
            const perms = response.data.permissions || [];
            const pagePerm = perms.find(p => p.pageId === 'leave_allocation');
            if (pagePerm) {
              setCanWrite(pagePerm.canWrite === true);
              setCanDelete(pagePerm.canDelete === true);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };

    const fetchBranches = async () => {
      try {
        const userStr = localStorage.getItem('user');
        const headers = {};
        if (userStr) {
          const userData = JSON.parse(userStr);
          if (userData.company_name) headers['X-Company-Name'] = userData.company_name;
        }
        const currentBaseUrl = baseUrl || '';
        const response = await axios.get(`${currentBaseUrl}/api/branches`, { headers });
        const branchData = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
        setAvailableBranches(branchData);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchPermissions();
    fetchBranches();
  }, [baseUrl, selectedBranchFilter]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get("/api/network_info");
        const { config: appConfig } = response.data;
        if (appConfig.mode === "client") {
          setBaseUrl(`http://${appConfig.server_ip}:6034`);
        } else {
          setBaseUrl('');
        }
      } catch (error) {
        console.error("Failed to fetch config:", error);
        setBaseUrl(window.location.origin || '');
      }
    };
    fetchConfig();
  }, []);

  const baseUrlResolved = baseUrl || '';

  useEffect(() => {
    if (baseUrlResolved !== undefined) {
      fetchInitialData();
    }
  }, [baseUrlResolved]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchLeaveTypes(), fetchAllocations()]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get(`${baseUrlResolved}/api/leave-types`, { headers: getHeaders() });
      const activeTypes = (res.data || []).filter(type => type.is_active);
      setLeaveTypes(activeTypes);
    } catch (err) {
      console.error('Error fetching leave types:', err);
    }
  };

  const fetchAllocations = async () => {
    try {
      const res = await axios.get(`${baseUrlResolved}/api/leave-allocations`, { headers: getHeaders() });
      setAllocations(res.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching allocations:', err);
      if (err.response && err.response.status !== 404) {
        setError('Failed to load leave allocations.');
      }
    }
  };

  const handleFocus = (field) => {
    if (form[field] === 0 || form[field] === '0') {
      setForm(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field) => {
    if (form[field] === '' || form[field] === null || form[field] === undefined) {
      setForm(prev => ({ ...prev, [field]: 0 }));
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canWrite) {
      setError("You do not have permission to manage leave allocations.");
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (showCompanyAssign || showBranchAssign) {
      setShowAssignModal(true);
    } else {
      processSubmit();
    }
  };

  const processSubmit = async () => {
    const activeContext = localStorage.getItem('active_company');
    const userStrLocal = localStorage.getItem('user');
    const userDataLocal = userStrLocal ? JSON.parse(userStrLocal) : null;
    const userCompLocal = userDataLocal ? (userDataLocal.company_name || userDataLocal.company) : "";
    const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
    const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';

    let companiesToProcess = showCompanyAssign
      ? selectedCompanies
      : (activeContext === 'All' ? selectedCompanies : [activeContext || userCompLocal || ""]);
    const branchesToProcess = isSpecificBranchActive ? [activeBranchLocal] : selectedAddBranches;

    if (companiesToProcess.length === 0) {
      companiesToProcess = [userCompLocal || ""];
    }

    const payload = {
      ...form,
      monthly_credit: Number(form.monthly_credit) || 0,
      yearly_credit: Number(form.yearly_credit) || 0,
      company_names: companiesToProcess,
      company_name: companiesToProcess[0] || "",
      branch_names: branchesToProcess,
      branch_name: branchesToProcess[0] || ""
    };

    setLoading(true);
    setError('');
    setShowAssignModal(false);

    try {
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : null;
      const headers = {};
      if (userData) {
        if (userData.company_name) headers['X-Company-Name'] = userData.company_name;
        const branchToUse = selectedBranchFilter || userData.branch_name;
        if (branchToUse) headers['X-Branch-Name'] = branchToUse;
      }
      if (editingId) {
        await axios.put(`${baseUrlResolved}/api/leave-allocations/${editingId}`, payload, { headers });
        setMessage('Allocation updated successfully!');
      } else {
        await axios.post(`${baseUrlResolved}/api/leave-allocations`, payload, { headers });
        setMessage('Allocation created successfully!');
      }
      setTimeout(() => setMessage(''), 2000);
      clearForm();
      fetchAllocations();
    } catch (err) {
      console.error("API Error details:", err.response);
      const errorMessage = err.response?.data?.error || 'Operation failed. Please try again.';
      setError(errorMessage);
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (alloc) => {
    setForm({
      leave_type_id: alloc.leave_type_id,
      monthly_credit: alloc.monthly_credit,
      yearly_credit: alloc.yearly_credit,
      credit_unit: alloc.credit_unit,
      is_active: alloc.is_active
    });
    setEditingId(alloc.allocation_id || alloc._id);
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!canDelete) {
      setError("You do not have permission to delete leave allocations.");
      setTimeout(() => setError(''), 3000);
      setShowDeleteConfirm(false);
      return;
    }
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : null;
      const headers = {};
      if (userData) {
        if (userData.company_name) headers['X-Company-Name'] = userData.company_name;
        const branchToUse = selectedBranchFilter || userData.branch_name;
        if (branchToUse) headers['X-Branch-Name'] = branchToUse;
      }
      await axios.delete(`${baseUrlResolved}/api/leave-allocations/${deletingId}`, { headers });
      setMessage('Allocation deleted successfully!');
      setTimeout(() => setMessage(''), 2000);
      setShowDeleteConfirm(false);
      fetchAllocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete allocation.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm(false);
      setLoading(false);
    }
  };

  const closeDeleteConfirm = (e) => {
    if (e && e.target !== e.currentTarget) return;
    setShowDeleteConfirm(false);
    setDeletingId(null);
  };

  const clearForm = () => {
    setForm({ leave_type_id: '', monthly_credit: 0, yearly_credit: 0, credit_unit: 'DAYS', is_active: true });
    setEditingId(null);
    setMessage('');
    setError('');
  };

  useEffect(() => {
    const fetchDynamicBranches = async () => {
      try {
        const url = baseUrl || "";
        const compsToFetch = selectedCompanies.length > 0 ? selectedCompanies : allCompanies;
        if (!compsToFetch || compsToFetch.length === 0) return;
        const localGetHeaders = () => {
          const token = localStorage.getItem('token');
          return token ? { Authorization: `Bearer ${token}` } : {};
        };
        const headersFunc = typeof getHeaders === 'function' ? getHeaders : localGetHeaders;
        const branchPromises = compsToFetch.map(comp =>
          axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, {
            headers: { ...headersFunc(), "X-Company-Name": comp }
          }).then(res => res.data)
        );
        const results = await Promise.all(branchPromises);
        const allBranches = [];
        results.forEach(data => {
          const branches = data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name)).filter(Boolean);
          allBranches.push(...branches);
        });
        setAvailableBranches([...new Set(allBranches)]);
      } catch (err) {
        console.error("Error fetching dynamic branches:", err);
      }
    };
    fetchDynamicBranches();
  }, [selectedCompanies, allCompanies, baseUrl]);

  if (loading && allocations.length === 0 && leaveTypes.length === 0) {
    return (
      <div className="create-customer-container main-customer-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', color: '#604be8', fontSize: '18px' }}>
          <FaClipboardList style={{ fontSize: '48px', marginBottom: '20px' }} />
          <p>Loading allocations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-customer-container main-customer-page">
      {/* Header */}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => navigate('/admin')} className="header-back-btn" disabled={loading}>
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1>Leave Allocation ({allocations.length})</h1>
        </div>
        <div className="header-right">
          <div className="header-actions">
            {(isGroupAdmin || isCompanyAdmin) && (
              <button onClick={() => setShowCustomizeModal(true)} className="save-btn" style={{ backgroundColor: '#00d284', width: 'auto', padding: '0 20px' }} disabled={loading}>
                <FaCog /> Customize
              </button>
            )}
            <button onClick={() => navigate('/leave-type')} className="save-btn" style={{ backgroundColor: '#4B76E6', width: 'auto', padding: '0 20px' }} disabled={loading}>
              <FaClipboardList /> Go to Leave Type
            </button>
            <button type="submit" form="leaveAllocationForm" disabled={loading || !form.leave_type_id} className="save-btn" style={{ backgroundColor: '#20c997', width: 'auto', padding: '0 20px' }}>
              {loading ? 'Saving...' : editingId ? 'Update Allocation' : 'Create Allocation'}
            </button>
          </div>
        </div>
      </div>

      <div className="customer-scroll-body" ref={containerRef}>
        <div className="form-sections-wrapper">

          {/* Error Message */}
          {error && (
            <div style={{ background: '#fef2f2', color: '#ef4444', padding: '16px 20px', borderRadius: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600', border: '1px solid #fee2e2' }}>
              <FaExclamationTriangle style={{ fontSize: '20px' }} /> {error}
            </div>
          )}

          {/* Success Message Popup */}
          {message && (
            <div onClick={() => setMessage('')} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, cursor: 'pointer' }}>
              <div onClick={(e) => e.stopPropagation()} style={{ background: '#ffffff', padding: '40px 32px', borderRadius: '24px', width: '90%', maxWidth: '440px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', cursor: 'default' }}>
                <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: '#ecfdf5', color: '#10b981', border: '2px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px' }}>
                  <FaCheckCircle />
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0' }}>Success</h3>
                <p style={{ fontSize: '16px', color: '#64748b', margin: '0', fontWeight: '500' }}>{message}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form id="leaveAllocationForm" onSubmit={handleSubmit}>
            <div className="form-section-card theme-blue" style={{ marginBottom: '24px' }}>
              <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <div className="section-icon" style={{ background: '#e0e7ff', color: '#4f46e5', width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  <FaClipboardList />
                </div>
                <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Leave Allocation Details</h2>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {isFieldVisible('leave_type_id') && (
                  <div className="form-field-group">
                    <label className="field-label">Select Leave Type</label>
                    <select
                      className="field-input"
                      value={form.leave_type_id}
                      onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}
                      required
                    >
                      <option value="">-- Select Leave Type --</option>
                      {leaveTypes.map(type => (
                        <option key={type.leave_type_id || type._id} value={type.leave_type_id || type._id}>
                          {type.leave_code} - {type.leave_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {isFieldVisible('credit_unit') && (
                  <div className="form-field-group">
                    <label className="field-label">Unit</label>
                    <select
                      className="field-input"
                      value={form.credit_unit}
                      onChange={(e) => setForm({ ...form, credit_unit: e.target.value })}
                    >
                      <option value="DAYS">Days</option>
                      <option value="MINUTES">Minutes</option>
                    </select>
                  </div>
                )}
                {isFieldVisible('monthly_credit') && (
                  <div className="form-field-group">
                    <label className="field-label">Monthly Credit</label>
                    <input
                      type="number"
                      className="field-input"
                      min="0"
                      step="0.01"
                      value={form.monthly_credit}
                      onChange={(e) => handleChange('monthly_credit', e.target.value)}
                      onFocus={() => handleFocus('monthly_credit')}
                      onBlur={() => handleBlur('monthly_credit')}
                      required
                    />
                  </div>
                )}
                {isFieldVisible('yearly_credit') && (
                  <div className="form-field-group">
                    <label className="field-label">Yearly Credit</label>
                    <input
                      type="number"
                      className="field-input"
                      min="0"
                      step="0.01"
                      value={form.yearly_credit}
                      onChange={(e) => handleChange('yearly_credit', e.target.value)}
                      onFocus={() => handleFocus('yearly_credit')}
                      onBlur={() => handleBlur('yearly_credit')}
                      required
                    />
                  </div>
                )}
                {isFieldVisible('is_active') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      id="is_active_alloc"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#604be8', cursor: 'pointer' }}
                    />
                    <label htmlFor="is_active_alloc" style={{ fontWeight: '500', color: '#2c3e50', cursor: 'pointer' }}>Active</label>
                  </div>
                )}
              </div>

              {editingId && (
                <div style={{ marginTop: '20px' }}>
                  <button type="button" onClick={clearForm} className="cancel-btn">Cancel Edit</button>
                </div>
              )}
            </div>
          </form>

          {/* Table */}
          {allocations.length > 0 ? (
            <div className="premium-table-wrapper" style={{ marginTop: '30px' }}>
              <table className="premium-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    {isFieldVisible('leave_type_id') && <th>Leave Type</th>}
                    {isFieldVisible('leave_type_id') && <th>Code</th>}
                    {isFieldVisible('company') && <th>Companies</th>}
                    {isFieldVisible('branch') && <th>Branches</th>}
                    {isFieldVisible('monthly_credit') && <th style={{ textAlign: 'center' }}>Monthly</th>}
                    {isFieldVisible('yearly_credit') && <th style={{ textAlign: 'center' }}>Yearly</th>}
                    {isFieldVisible('credit_unit') && <th style={{ textAlign: 'center' }}>Unit</th>}
                    {isFieldVisible('is_active') && <th style={{ textAlign: 'center' }}>Status</th>}
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((alloc) => (
                    <tr key={alloc.allocation_id || alloc._id}>
                      {isFieldVisible('leave_type_id') && <td>{alloc.leave_name || 'N/A'}</td>}
                      {isFieldVisible('leave_type_id') && <td>{alloc.leave_code || 'N/A'}</td>}
                      {isFieldVisible('company') && <td className="bold-text">{(alloc.company_names || (alloc.company_name ? [alloc.company_name] : [])).join(', ') || 'All'}</td>}
                      {isFieldVisible('branch') && <td className="bold-text">{(alloc.branch_names || (alloc.branch_name ? [alloc.branch_name] : [])).join(', ') || 'All Branches'}</td>}
                      {isFieldVisible('monthly_credit') && <td style={{ textAlign: 'center' }}>{alloc.monthly_credit}</td>}
                      {isFieldVisible('yearly_credit') && <td style={{ textAlign: 'center' }}>{alloc.yearly_credit}</td>}
                      {isFieldVisible('credit_unit') && <td style={{ textAlign: 'center' }}>{alloc.credit_unit}</td>}
                      {isFieldVisible('is_active') && (
                        <td style={{ textAlign: 'center' }}>
                          <span className={`status-badge ${alloc.is_active ? 'active' : 'inactive'}`}>
                            {alloc.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      <td>
                        <div className="action-buttons">
                          <button onClick={() => handleEdit(alloc)} className="action-btn edit" title="Edit">
                            <FaEdit />
                          </button>
                          <button onClick={() => handleDelete(alloc.allocation_id || alloc._id)} className="action-btn delete" title="Delete" disabled={loading}>
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ marginTop: '20px', paddingLeft: '10px' }}>
              <h2 style={{ fontSize: '18px', color: '#1e293b', margin: 0, fontWeight: '600' }}>Allocation List</h2>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="add-modal-overlay" onClick={closeDeleteConfirm}>
          <div className="add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
              <span><FaTrash style={{ fontSize: '18px' }} /> Confirm Delete</span>
              <button className="close-modal-btn" onClick={closeDeleteConfirm}><FaTimes /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this allocation? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeDeleteConfirm} style={{ background: '#f1f5f9', color: '#64748b' }}>
                Cancel
              </button>
              <button className="danger-btn" onClick={confirmDelete} disabled={loading}>
                {loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Companies & Branches Modal */}
      {showAssignModal && (
        <div className="add-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="add-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span>
                <FaBuilding style={{ fontSize: '20px' }} />
                Assign Companies & Branches
              </span>
              <button className="close-modal-btn" onClick={() => setShowAssignModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {showCompanyAssign && (
                <div style={{ marginBottom: '24px' }}>
                  <p>Select Companies</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {(allCompanies.length > 0 ? allCompanies : selectedCompanies).map((comp, idx) => {
                      const isSelected = selectedCompanies.includes(comp);
                      return (
                        <div key={idx} style={{ border: isSelected ? '2px solid #604be8' : '2px solid #e2e8f0', padding: '12px 16px', borderRadius: '14px', backgroundColor: isSelected ? '#f5f3ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => {
                          if (isSelected) setSelectedCompanies(prev => prev.filter(c => c !== comp));
                          else setSelectedCompanies(prev => [...prev, comp]);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ width: '18px', height: '18px', accentColor: '#604be8', cursor: 'pointer' }} />
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{comp}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {showBranchAssign && selectedCompanies.length > 0 && availableBranches.length > 0 && (
                <div>
                  <p>Select Branches</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    {availableBranches.map((branch, idx) => {
                      const isSelected = selectedAddBranches.includes(branch);
                      return (
                        <div key={idx} style={{ border: isSelected ? '2px solid #604be8' : '2px solid #e2e8f0', padding: '12px 16px', borderRadius: '14px', backgroundColor: isSelected ? '#f5f3ff' : '#f8fafc', cursor: 'pointer', transition: 'all 0.2s' }} onClick={() => {
                          if (isSelected) setSelectedAddBranches(prev => prev.filter(b => b !== branch));
                          else setSelectedAddBranches(prev => [...prev, branch]);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ width: '18px', height: '18px', accentColor: '#604be8', cursor: 'pointer' }} />
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{branch}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowAssignModal(false)} style={{ background: '#f1f5f9', color: '#64748b' }}>
                Cancel
              </button>
              <button className="save-btn" onClick={processSubmit} disabled={loading} style={{ background: '#604be8' }}>
                {loading ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={handleCustomizationRefresh}
        targetDocType="Leave Allocation"
      />
    </div>
  );
};

export default LeaveAllocation;
